import { useEffect, useState, type ChangeEvent } from "react";
import type { SingleValue } from "react-select";

import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { SelectInput } from "components/Select";
import { Switch } from "components/ui/switch";
import { toast } from "components/ui/use-toast";

import { extractRtkErrorMessage } from "@/utils/extract-rtk-error";
import {
  useCreateStoreBankAccountMutation,
  useGetBanksQuery,
  useUpdateStoreBankAccountMutation,
} from "@/services/store-bank-account";
import type { Account } from "@/services/dtos/store-bank-account.dto";

import {
  ACCOUNT_FORM_DESCRIPTIONS,
  ACCOUNT_FORM_ERROR_MESSAGES,
  ACCOUNT_FORM_FIELD_IDS,
  ACCOUNT_FORM_FIELD_LIMITS,
  ACCOUNT_FORM_LABELS,
  ACCOUNT_FORM_MODE,
  ACCOUNT_FORM_PLACEHOLDERS,
  ACCOUNT_FORM_SUBMITTING_LABELS,
  ACCOUNT_FORM_SUBMIT_LABELS,
  ACCOUNT_FORM_SUCCESS_MESSAGES,
  ACCOUNT_FORM_TITLES,
  CANCEL_BUTTON_LABEL,
  buildCreateAccountBody,
  buildInitialFormValues,
  buildUpdateAccountBody,
  isAccountFormValid,
  requireAccountKey,
  requireStoreCode,
  toBankSelectOptions,
  toValidatedFormValues,
  validateAccountFormValues,
  type AccountFormErrors,
  type AccountFormMode,
  type AccountFormValues,
  type BankSelectOption,
  type ValidatedAccountFormValues,
} from "./types";

export interface AccountFormModalProps {
  open: boolean;
  mode: AccountFormMode;
  storeCode: number | null;
  account: Account | null;
  onClose: () => void;
}

type SubmitByMode = Record<
  AccountFormMode,
  (values: ValidatedAccountFormValues) => Promise<Account>
>;

export function AccountFormModal({
  open,
  mode,
  storeCode,
  account,
  onClose,
}: AccountFormModalProps) {
  const [values, setValues] = useState<AccountFormValues>(() =>
    buildInitialFormValues(mode, account),
  );
  const [errors, setErrors] = useState<AccountFormErrors>({});

  const { data: bankOptions = [] } = useGetBanksQuery(undefined);
  const [createStoreBankAccount, { isLoading: isCreating }] =
    useCreateStoreBankAccountMutation();
  const [updateStoreBankAccount, { isLoading: isUpdating }] =
    useUpdateStoreBankAccountMutation();

  useEffect(() => {
    if (!open) return;
    setValues(buildInitialFormValues(mode, account));
    setErrors({});
  }, [open, mode, account]);

  const isSubmitting = isCreating || isUpdating;
  const isEditMode = mode === ACCOUNT_FORM_MODE.EDIT;

  const submitByMode: SubmitByMode = {
    [ACCOUNT_FORM_MODE.CREATE]: (validated) =>
      createStoreBankAccount(
        buildCreateAccountBody(requireStoreCode(storeCode), validated),
      ).unwrap(),
    [ACCOUNT_FORM_MODE.EDIT]: (validated) =>
      updateStoreBankAccount(
        buildUpdateAccountBody(requireAccountKey(account), validated),
      ).unwrap(),
  };

  const handleBankChange = (option: SingleValue<BankSelectOption>): void => {
    setValues((prev) => ({ ...prev, bank: option ?? null }));
  };

  const handleAgencyChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setValues((prev) => ({ ...prev, agency: event.target.value }));
  };

  const handleAccountChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setValues((prev) => ({ ...prev, account: event.target.value }));
  };

  const handleActiveChange = (checked: boolean): void => {
    setValues((prev) => ({ ...prev, active: checked }));
  };

  const handleOpenChange = (next: boolean): void => {
    if (next) return;
    onClose();
  };

  const handleSubmit = async (): Promise<void> => {
    const validationErrors = validateAccountFormValues(values);
    setErrors(validationErrors);
    if (!isAccountFormValid(validationErrors)) return;

    const validated = toValidatedFormValues(values);
    if (!validated) return;

    try {
      await submitByMode[mode](validated);
      toast({ description: ACCOUNT_FORM_SUCCESS_MESSAGES[mode] });
      onClose();
    } catch (error) {
      toast({
        variant: "destructive",
        description: extractRtkErrorMessage(
          error,
          ACCOUNT_FORM_ERROR_MESSAGES.GENERIC_SAVE_ERROR,
        ),
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{ACCOUNT_FORM_TITLES[mode]}</DialogTitle>
          <DialogDescription>
            {ACCOUNT_FORM_DESCRIPTIONS[mode]}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <span
              id={ACCOUNT_FORM_FIELD_IDS.BANK_LABEL}
              className="text-sm font-medium"
            >
              {ACCOUNT_FORM_LABELS.BANK}
            </span>
            <SelectInput<BankSelectOption>
              options={toBankSelectOptions(bankOptions)}
              value={values.bank}
              onChange={handleBankChange}
              placeholder={ACCOUNT_FORM_PLACEHOLDERS.BANK}
              aria-labelledby={ACCOUNT_FORM_FIELD_IDS.BANK_LABEL}
              isClearable={false}
              isDisabled={isEditMode}
            />
            {errors.bank && (
              <span className="text-sm text-destructive">{errors.bank}</span>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor={ACCOUNT_FORM_FIELD_IDS.AGENCY}>
              {ACCOUNT_FORM_LABELS.AGENCY}
            </Label>
            <Input
              id={ACCOUNT_FORM_FIELD_IDS.AGENCY}
              value={values.agency}
              onChange={handleAgencyChange}
              maxLength={ACCOUNT_FORM_FIELD_LIMITS.AGENCY_MAX_LENGTH}
              hasError={Boolean(errors.agency)}
              disabled={isEditMode}
            />
            {errors.agency && (
              <span className="text-sm text-destructive">
                {errors.agency}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor={ACCOUNT_FORM_FIELD_IDS.ACCOUNT}>
              {ACCOUNT_FORM_LABELS.ACCOUNT}
            </Label>
            <Input
              id={ACCOUNT_FORM_FIELD_IDS.ACCOUNT}
              value={values.account}
              onChange={handleAccountChange}
              maxLength={ACCOUNT_FORM_FIELD_LIMITS.ACCOUNT_MAX_LENGTH}
              hasError={Boolean(errors.account)}
            />
            {errors.account && (
              <span className="text-sm text-destructive">
                {errors.account}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Switch
              id={ACCOUNT_FORM_FIELD_IDS.ACTIVE}
              checked={values.active}
              onCheckedChange={handleActiveChange}
            />
            <Label htmlFor={ACCOUNT_FORM_FIELD_IDS.ACTIVE}>
              {ACCOUNT_FORM_LABELS.ACTIVE}
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            {CANCEL_BUTTON_LABEL}
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting
              ? ACCOUNT_FORM_SUBMITTING_LABELS[mode]
              : ACCOUNT_FORM_SUBMIT_LABELS[mode]}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
